import type { ReactNode } from 'react'
import { NumberedItem } from '../../../../../../../components/ui-components/NumberedItem'
import type { LumpsumIdealPlanRecommendationApiResponse } from '../../api/idealRecommendation.types'
import { buildPlanAllocationRangeLabel, planPercentageLabel } from '../../../../display-values/lumpsumPlanPercentage'
import {
  ConsistencyScorePointer,
  PlanRecommendationReasonTextStrip,
  PowerRankPointer,
  RecencyScorePointer,
  VolatilityScorePointer
} from '../../../../plans-ui-components/PlanRecommendationScoreReasonPointers'

type IdealPlanReasonPointerKind =
  | 'MAINTAIN_EXPOSURE'
  | 'POWER_RANK'
  | 'CONSISTENCY_SCORE'
  | 'RECENCY_SCORE'
  | 'VOLATILITY_SCORE'

type IdealPlanScoreOrRankReasonPlanDetails = {
  powerRank?: number | null
  scoringData?: {
    consistencyScore?: number | null
    recencyScore?: number | null
    volatilityScore?: number | null
  } | null
} | null

export function collectIdealPlanScoreOrRankPointerKinds(
  planDetails: IdealPlanScoreOrRankReasonPlanDetails
): IdealPlanReasonPointerKind[] {
  if (!planDetails) {
    return []
  }

  const pointerKinds: IdealPlanReasonPointerKind[] = []
  const scoringData = planDetails?.scoringData

  if (planDetails?.powerRank != null) {
    pointerKinds.push('POWER_RANK')
  }
  if (scoringData?.consistencyScore != null) {
    pointerKinds.push('CONSISTENCY_SCORE')
  }
  if (scoringData?.recencyScore != null) {
    pointerKinds.push('RECENCY_SCORE')
  }
  if (scoringData?.volatilityScore != null) {
    pointerKinds.push('VOLATILITY_SCORE')
  }

  return pointerKinds
}

/** True when at least one Power Rank or score reason pointer would render. */
export function hasIdealPlanScoreOrRankReasonPointers(
  planDetails: IdealPlanScoreOrRankReasonPlanDetails
): boolean {
  return collectIdealPlanScoreOrRankPointerKinds(planDetails).length > 0
}

type IdealPlanReasonsToInvestProps = {
  planDetails: LumpsumIdealPlanRecommendationApiResponse | null
}

export function IdealPlanReasonsToInvest({ planDetails }: IdealPlanReasonsToInvestProps) {
  const pointers = buildIdealPlanReasonPointers(planDetails)

  if (pointers.length === 0) {
    return null
  }

  return (
    <div className="pt-6">
      <div className="px-5 pb-2 text-[18px] font-[500] leading-[130%] text-[#232323]">Reasons to invest</div>
      {pointers}
    </div>
  )
}

type IdealPlanPointerWrapperProps = {
  index: number
  borderBottom?: boolean
}

function buildIdealPlanReasonPointers(
  planDetails: LumpsumIdealPlanRecommendationApiResponse | null
): ReactNode[] {
  if (!planDetails) {
    return []
  }

  const categoryLabel = planDetails?.categoryName ?? ''
  const fundName = planDetails?.fundName ?? ''
  const fundIdealAbsolute = planDetails?.fundIdealAllocation?.absolute ?? 0
  const fundAllocationLabel = planPercentageLabel({ rawPercentage: fundIdealAbsolute })
  const categoryAllocationRangeLabel = buildPlanAllocationRangeLabel({ idealAllocation: planDetails?.categoryIdealAllocation })
  const pointerKinds: IdealPlanReasonPointerKind[] = []

  if (
    fundIdealAbsolute > 0 &&
    categoryLabel.length > 0 &&
    categoryAllocationRangeLabel.length > 0 &&
    fundName.length > 0
  ) {
    pointerKinds.push('MAINTAIN_EXPOSURE')
  }
  pointerKinds.push(...collectIdealPlanScoreOrRankPointerKinds(planDetails))

  return pointerKinds.map((pointerKind, pointerIndex) => {
    const pointerNumber = pointerIndex + 1
    const borderBottom = pointerIndex < pointerKinds.length - 1

    return renderIdealPlanReasonPointer({
      pointerKind,
      index: pointerNumber,
      borderBottom,
      planDetails,
      categoryLabel,
      fundName,
      fundAllocationLabel,
      categoryAllocationRangeLabel
    })
  })
}

function renderIdealPlanReasonPointer({
  pointerKind,
  index,
  borderBottom,
  planDetails,
  categoryLabel,
  fundName,
  fundAllocationLabel,
  categoryAllocationRangeLabel
}: {
  pointerKind: IdealPlanReasonPointerKind
  index: number
  borderBottom: boolean
  planDetails: LumpsumIdealPlanRecommendationApiResponse
  categoryLabel: string
  fundName: string
  fundAllocationLabel: string
  categoryAllocationRangeLabel: string
}): ReactNode {
  if (pointerKind === 'MAINTAIN_EXPOSURE') {
    return (
      <MaintainExposurePointer
        key={pointerKind}
        index={index}
        borderBottom={borderBottom}
        categoryLabel={categoryLabel}
        fundName={fundName}
        fundAllocationLabel={fundAllocationLabel}
        categoryAllocationRangeLabel={categoryAllocationRangeLabel}
      />
    )
  }

  if (pointerKind === 'POWER_RANK') {
    return (
      <PowerRankPointer
        key={pointerKind}
        index={index}
        borderBottom={borderBottom}
        categoryLabel={categoryLabel}
        powerRank={planDetails?.powerRank ?? null}
        powerRating={planDetails?.powerRating ?? ''}
        totalCategoryCount={planDetails?.totalCategoryCount ?? null}
      />
    )
  }

  const scoringData = planDetails?.scoringData
  const maxScore = scoringData?.maxScore ?? 0

  if (pointerKind === 'CONSISTENCY_SCORE') {
    return (
      <ConsistencyScorePointer
        key={pointerKind}
        index={index}
        borderBottom={borderBottom}
        categoryLabel={categoryLabel}
        score={scoringData?.consistencyScore}
        maxScore={maxScore}
        performanceTag={scoringData?.consistencyScorePerformanceTag ?? ''}
      />
    )
  }

  if (pointerKind === 'RECENCY_SCORE') {
    return (
      <RecencyScorePointer
        key={pointerKind}
        index={index}
        borderBottom={borderBottom}
        categoryLabel={categoryLabel}
        score={scoringData?.recencyScore}
        maxScore={maxScore}
        performanceTag={scoringData?.recencyScorePerformanceTag ?? ''}
      />
    )
  }

  return (
    <VolatilityScorePointer
      key={pointerKind}
      index={index}
      borderBottom={borderBottom}
      categoryLabel={categoryLabel}
      score={scoringData?.volatilityScore}
      maxScore={maxScore}
      performanceTag={scoringData?.volatilityScorePerformanceTag ?? ''}
    />
  )
}

export function MaintainExposurePointer({
  index,
  borderBottom,
  categoryLabel,
  fundName,
  fundAllocationLabel,
  categoryAllocationRangeLabel
}: IdealPlanPointerWrapperProps & {
  categoryLabel: string
  fundName: string
  fundAllocationLabel: string
  categoryAllocationRangeLabel: string
}) {
  return (
    <NumberedItem
      number={index}
      title={`Helps maintain ${categoryLabel} exposure`}
      subtitle={`within ideal range of ${categoryAllocationRangeLabel}, as per allocation strategy`}
      borderBottom={borderBottom}
    >
      <PlanRecommendationReasonTextStrip
        highlightTitle={fundAllocationLabel}
        highlightDescription={`<span style="font-weight: 500">in ${categoryLabel}</span> by maintaining investment in ${fundName}`}
      />
    </NumberedItem>
  )
}
